import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useRouter } from 'next/router';
import { CompanyProfileTabsWrapper }
  from '@fuww/library/src/CompanyProfile';
import { NavTabs } from '@fuww/library/src/Tabs';
import { useIntl } from 'react-intl';
import productsQuery
  from '../../lib/queries/products/companyProfileProductsQuery.graphql';
import Tab from '../Tab';
import withQuery from '../../lib/withQuery';
import messages from '../../lib/messages.json';
import { useSiteContext } from '../SiteContext';

const companyTabsPropertyTypes = {
  profile: PropTypes.shape({
    brands: PropTypes.arrayOf(PropTypes.shape({}).isRequired).isRequired,
    page: PropTypes.shape({
      title: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  data: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.shape({}).isRequired).isRequired,
    hiddenProductsExist: PropTypes.bool,
  }).isRequired,
};

type CompanyTabsProperties = InferProps<typeof companyTabsPropertyTypes>;

const CompanyTabs = ({ data, profile }: CompanyTabsProperties) => {
  const intl = useIntl();
  const { query: { slug }, route: currentRoute } = useRouter();
  const { products, hiddenProductsExist } = data;
  const { brands } = profile;

  return (
    <CompanyProfileTabsWrapper>
      {
      brands.length > 0
      && (hiddenProductsExist || products.length > 0)
      && (
        <NavTabs
          value={currentRoute}
          NavTab={Tab}
          tabs={[
            {
              route: 'company-profile',
              params: { slug },
              value: '/company-profile',
              label: intl.formatMessage(messages.about),
            },
            {
              route: 'company-profile-products',
              params: { slug },
              value: '/company-profile-products',
              label: intl.formatMessage(messages.products),
            },
          ]}
        />
      )
    }
    </CompanyProfileTabsWrapper>
  );
};

CompanyTabs.propTypes = companyTabsPropertyTypes;

const CompanyTabsWithQuery = withQuery(CompanyTabs);

const companyTabsWrapperPropertyTypes = {
  profile: PropTypes.shape({
    brands: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
    }).isRequired).isRequired,
  }).isRequired,
};

type CompanyTabsWrapperProperties = InferProps<
  typeof companyTabsWrapperPropertyTypes
>;

const CompanyTabsWrapper = ({
  profile,
}: CompanyTabsWrapperProperties) => {
  const { locale } = useSiteContext();

  return (
    <CompanyTabsWithQuery
      profile={profile}
      query={productsQuery}
      variables={{
        marketId: 1,
        brandIds: profile.brands.map(({ id }) => id),
        mainCategories: [],
        locales: [locale],
        limit: 1,
      }}
      errorMessage="Error loading company tab"
    />
  );
};

CompanyTabsWrapper.propTypes = companyTabsWrapperPropertyTypes;

const schoolTabsPropertyTypes = {
  profile: PropTypes.shape({
    coursesConnection: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({}).isRequired).isRequired,
    }).isRequired,
  }).isRequired,
};

type SchoolTabsProperties = InferProps<typeof schoolTabsPropertyTypes>;

const SchoolTabs = ({ profile }: SchoolTabsProperties) => {
  const intl = useIntl();
  const { query: { slug }, route: currentRoute } = useRouter();
  const { coursesConnection: { edges } } = profile;

  return (
    <CompanyProfileTabsWrapper>
      {edges.length > 0 && (
      <NavTabs
        value={currentRoute}
        NavTab={Tab}
        tabs={[
          {
            route: 'company-profile',
            params: { slug },
            value: '/company-profile',
            label: intl.formatMessage(messages.about),
          },
          {
            route: 'school-profile-courses',
            params: { slug },
            value: '/school-profile-courses',
            label: 'courses',
          },
        ]}
      />
      )}
    </CompanyProfileTabsWrapper>
  );
};

SchoolTabs.propTypes = schoolTabsPropertyTypes;

export { SchoolTabs, CompanyTabsWrapper };
